/**
 * Kunden — Kundenliste mit CRUD, Segment-Badge, Odoo-Matching und Standort-Zuordnung.
 *
 * Features:
 * - Kunden anlegen, bearbeiten, loeschen
 * - Odoo-Partner-Suche und Auto-Match
 * - Segment-Anzeige (A/B/C), Margen-Badge
 * - Standort-Filter, Suche nach Name/Kuerzel
 *
 * @page /kunden
 * @roles admin, manager
 * @api /api/kunden, /api/kunden/:id, /api/kunden/odoo-match
 * @api /api/odoo/partner-search, /api/standorte
 * @i18n customers
 */
import { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { PageHeader, Badge, Button } from '../components/ui';
import { StatusBadge } from '../components/ui/StatusBadge';
import { useLanguage } from '../context/LanguageContext';

// ─── Helpers ───────────────────────────────────────────────────────

function api(path, opts = {}) {
  return fetch(path, {
    headers: { 'Content-Type': 'application/json' },
    ...opts,
    body: opts.body ? JSON.stringify(opts.body) : undefined,
  }).then((r) => r.json());
}

const segColor = {
  A: 'bg-primary-500/15 text-primary-400',
  B: 'bg-purple-500/15 text-purple-400',
  C: 'bg-amber-500/15 text-amber-400',
};

function margeBg(pct) {
  if (pct >= 25) return 'bg-green-500/15 text-green-400';
  if (pct >= 15) return 'bg-yellow-500/15 text-yellow-400';
  if (pct >= 10) return 'bg-orange-500/15 text-orange-400';
  return 'bg-red-500/15 text-red-400';
}

// ─── UI Components ─────────────────────────────────────────────────

function Modal({ open, onClose, title, wide, children }) {
  if (!open) return null;
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" onClick={onClose} />
      <div className={`relative bg-surface-elevated border border-line-hover rounded-card w-full ${wide ? 'max-w-3xl' : 'max-w-lg'} max-h-[85vh] overflow-y-auto shadow-2xl`}>
        <div className="flex items-center justify-between p-5 border-b border-line">
          <h2 className="text-lg font-bold">{title}</h2>
          <button onClick={onClose} className="text-[var(--text-tertiary)] hover:text-content text-xl leading-none">&times;</button>
        </div>
        <div className="p-5">{children}</div>
      </div>
    </div>
  );
}

function Field({ label, children }) {
  return (
    <label className="block mb-4">
      <span className="text-xs font-medium text-[var(--text-secondary)] uppercase tracking-wider">{label}</span>
      <div className="mt-1.5">{children}</div>
    </label>
  );
}

function Input(props) {
  return (
    <input
      {...props}
      className="w-full bg-surface-inset border border-line-hover rounded-lg px-3 py-2 text-sm text-content placeholder-content-dim focus:outline-none focus:border-line-hover focus:ring-1 focus:ring-primary-500/30"
    />
  );
}

function Select({ children, ...props }) {
  return (
    <select
      {...props}
      className="w-full bg-surface-inset border border-line-hover rounded-lg px-3 py-2 text-sm text-content focus:outline-none focus:border-line-hover"
    >
      {children}
    </select>
  );
}

// ─── Odoo Partner-Picker ────────────────────────────────────────────

function OdooPartnerPicker({ value, onChange, kundeName, t }) {
  const [searching, setSearching] = useState(false);
  const [results, setResults] = useState(null);
  const [searchName, setSearchName] = useState('');

  const doSearch = async (name) => {
    if (!name) return;
    setSearching(true);
    setResults(null);
    try {
      const data = await api(`/api/odoo/partner-search?name=${encodeURIComponent(name)}`);
      setResults(data);
    } catch { setResults({ partners: [], hinweis: t('customers.errorPrefix', { msg: 'Search failed' }) }); }
    finally { setSearching(false); }
  };

  return (
    <div className="space-y-2">
      <div className="flex gap-2">
        <input
          type="number"
          value={value || ''}
          onChange={(e) => onChange(e.target.value)}
          placeholder={t('customers.odooPartnerIdPlaceholder')}
          className="flex-1 bg-surface-inset border border-line-hover rounded-lg px-3 py-2 text-sm text-content placeholder-content-dim focus:outline-none focus:border-line-hover focus:ring-1 focus:ring-primary-500/30"
        />
        <button
          type="button"
          onClick={() => { setSearchName(kundeName); doSearch(kundeName); }}
          disabled={searching || !kundeName}
          className="px-3 py-2 rounded-lg text-xs font-medium bg-cyan-600/20 text-cyan-400 hover:bg-cyan-600/30 border border-cyan-500/20 transition-colors disabled:opacity-40 whitespace-nowrap"
        >
          {searching ? (
            <span className="flex items-center gap-1.5">
              <span className="animate-spin h-3 w-3 border border-cyan-400 border-t-transparent rounded-full" />
              {t('customers.searching')}
            </span>
          ) : t('customers.autoMatch')}
        </button>
      </div>

      {/* Manuelle Suche */}
      {results !== null && (
        <div className="bg-surface-card border border-line rounded-card p-3 space-y-2 shadow-card">
          <div className="flex gap-2">
            <input
              type="text"
              value={searchName}
              onChange={(e) => setSearchName(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && (e.preventDefault(), doSearch(searchName))}
              placeholder={t('customers.changeSearchTerm')}
              className="flex-1 bg-surface-inset border border-line-hover rounded px-2 py-1 text-xs text-content placeholder-content-dim focus:outline-none"
            />
            <button type="button" onClick={() => doSearch(searchName)} disabled={searching} className="px-2 py-1 text-[10px] bg-surface-inset hover:bg-surface-active rounded text-content-tertiary transition-colors">
              {t('customers.search')}
            </button>
          </div>

          {results.hinweis && <div className="text-[10px] text-yellow-400">{results.hinweis}</div>}

          {results.partners && results.partners.length > 0 ? (
            <div className="space-y-1">
              {results.partners.map(p => (
                <button
                  key={p.id}
                  type="button"
                  onClick={() => { onChange(String(p.id)); setResults(null); }}
                  className={`w-full text-left px-2.5 py-1.5 rounded text-xs transition-colors ${
                    String(value) === String(p.id) ? 'bg-cyan-500/20 text-cyan-300 border border-cyan-500/30' : 'hover:bg-surface-hover text-content-secondary'
                  }`}
                >
                  <div className="flex items-center justify-between">
                    <span>
                      {p.is_company && <span className="text-[10px] text-[var(--text-secondary)] mr-1">{t('customers.company')}</span>}
                      {p.name}
                    </span>
                    <span className="text-[10px] text-[var(--text-secondary)] font-mono">ID: {p.id}</span>
                  </div>
                  {(p.email || p.phone) && (
                    <div className="text-[10px] text-[var(--text-secondary)] mt-0.5">{[p.email, p.phone].filter(Boolean).join(' · ')}</div>
                  )}
                </button>
              ))}
            </div>
          ) : !results.hinweis && (
            <div className="text-[10px] text-[var(--text-secondary)] py-1">{t('customers.noResultsSearch')}</div>
          )}

          <button type="button" onClick={() => setResults(null)} className="text-[10px] text-[var(--text-tertiary)] hover:text-content-tertiary">{t('customers.close')}</button>
        </div>
      )}

      {value && <div className="text-[10px] text-[var(--text-secondary)]">Odoo Partner-ID: <span className="text-cyan-400 font-mono">{value}</span></div>}
    </div>
  );
}

// ─── Form ──────────────────────────────────────────────────────────

function KundenForm({ initial, standorte, onSave, onCancel, t }) {
  const [f, setF] = useState(initial || {
    name: '', standort_id: standorte[0]?.id || '', segment: 'B',
    picking_time_per_paket: 2.0, packing_time_per_paket: 3.0,
    cutoff_stunde: 12.0, vor_cutoff_pct: 60.0, odoo_partner_id: '',
  });
  const set = (k, v) => setF((p) => ({ ...p, [k]: v }));

  const submit = (e) => {
    e.preventDefault();
    onSave({
      ...f,
      standort_id: Number(f.standort_id) || null,
      picking_time_per_paket: Number(f.picking_time_per_paket),
      packing_time_per_paket: Number(f.packing_time_per_paket),
      cutoff_stunde: Number(f.cutoff_stunde),
      vor_cutoff_pct: Number(f.vor_cutoff_pct),
      odoo_partner_id: f.odoo_partner_id ? Number(f.odoo_partner_id) : null,
    });
  };

  return (
    <form onSubmit={submit}>
      <div className="grid grid-cols-2 gap-x-4">
        <Field label={t('customers.name')}><Input value={f.name} onChange={(e) => set('name', e.target.value)} required /></Field>
        <Field label={t('customers.segment')}>
          <Select value={f.segment} onChange={(e) => set('segment', e.target.value)}>
            <option value="A">{t('customers.segA')}</option>
            <option value="B">{t('customers.segB')}</option>
            <option value="C">{t('customers.segC')}</option>
          </Select>
        </Field>
      </div>
      <Field label={t('customers.location')}>
        <Select value={f.standort_id} onChange={(e) => set('standort_id', e.target.value)}>
          <option value="">{t('customers.noLocation')}</option>
          {standorte.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
        </Select>
      </Field>
      <div className="grid grid-cols-2 gap-x-4">
        <Field label={t('customers.pickMinPerPkg')}><Input type="number" step="0.1" value={f.picking_time_per_paket} onChange={(e) => set('picking_time_per_paket', e.target.value)} /></Field>
        <Field label={t('customers.packMinPerPkg')}><Input type="number" step="0.1" value={f.packing_time_per_paket} onChange={(e) => set('packing_time_per_paket', e.target.value)} /></Field>
      </div>
      <div className="grid grid-cols-2 gap-x-4">
        <Field label={t('customers.cutoffHour')}><Input type="number" step="0.5" value={f.cutoff_stunde} onChange={(e) => set('cutoff_stunde', e.target.value)} /></Field>
        <Field label={t('customers.beforeCutoffPct')}><Input type="number" step="1" value={f.vor_cutoff_pct} onChange={(e) => set('vor_cutoff_pct', e.target.value)} /></Field>
      </div>
      <Field label={t('customers.odooPartnerId')}>
        <OdooPartnerPicker value={f.odoo_partner_id || ''} onChange={(v) => set('odoo_partner_id', v)} kundeName={f.name} t={t} />
      </Field>
      <div className="flex justify-end gap-3 mt-2">
        <button type="button" onClick={onCancel} className="px-4 py-2 rounded-lg text-sm text-content-tertiary hover:text-content hover:bg-surface-hover transition-colors">{t('customers.cancel')}</button>
        <button type="submit" className="px-4 py-2 rounded-lg text-sm font-semibold bg-surface-active text-content hover:bg-surface-active transition-colors">{t('customers.save')}</button>
      </div>
    </form>
  );
}

// ═══════════════════════════════════════════════════════════════════
// PAGE
// ═══════════════════════════════════════════════════════════════════

export default function KundenPage() {
  const navigate = useNavigate();
  const { t } = useLanguage();
  const [data, setData] = useState([]);
  const [standorte, setStandorte] = useState([]);
  const [modal, setModal] = useState(null);

  // Sorting & filtering
  const [searchText, setSearchText] = useState('');
  const [activeSegments, setActiveSegments] = useState([]);
  const [activeFulfillment, setActiveFulfillment] = useState([]);
  const [standortFilter, setStandortFilter] = useState('');
  const [sortCol, setSortCol] = useState('name');
  const [sortDir, setSortDir] = useState('asc');

  // Bulk-Match State
  const [bulkModal, setBulkModal] = useState(false);
  const [bulkLoading, setBulkLoading] = useState(false);
  const [bulkResult, setBulkResult] = useState(null);

  useEffect(() => { api('/api/standorte').then(setStandorte); }, []);

  const load = useCallback(() => { api('/api/kunden').then(setData); }, []);
  useEffect(() => { load(); }, [load]);

  const save = async (k) => {
    if (k.id) {
      const { name, standort_id, segment, picking_time_per_paket, packing_time_per_paket, cutoff_stunde, vor_cutoff_pct, odoo_partner_id } = k;
      await api(`/api/kunden/${k.id}`, { method: 'PUT', body: { name, standort_id, segment, picking_time_per_paket, packing_time_per_paket, cutoff_stunde, vor_cutoff_pct, odoo_partner_id } });
    } else {
      await api('/api/kunden', { method: 'POST', body: k });
    }
    setModal(null);
    load();
  };

  const remove = async (id) => {
    await api(`/api/kunden/${id}`, { method: 'DELETE' });
    load();
  };

  const runBulkMatch = async () => {
    setBulkLoading(true);
    setBulkResult(null);
    try {
      const result = await api('/api/kunden/odoo-match', { method: 'POST', body: {} });
      setBulkResult(result);
      load();
    } catch (e) {
      setBulkResult({ message: t('customers.errorPrefix', { msg: e.message }), matched: 0, manual: [] });
    }
    finally { setBulkLoading(false); }
  };

  const assignManual = async (kundeId, partnerId) => {
    await api(`/api/kunden/${kundeId}`, { method: 'PUT', body: { odoo_partner_id: partnerId } });
    setBulkResult(prev => {
      if (!prev) return prev;
      return { ...prev, manual: prev.manual.map(m => m.kunde_id === kundeId ? { ...m, status: 'zugewiesen', zugewiesene_id: partnerId } : m) };
    });
    load();
  };

  const toggleSegment = (seg) => {
    setActiveSegments(prev => prev.includes(seg) ? prev.filter(s => s !== seg) : [...prev, seg]);
  };

  const toggleFulfillment = (status) => {
    setActiveFulfillment(prev => prev.includes(status) ? prev.filter(s => s !== status) : [...prev, status]);
  };

  const toggleSort = (col) => {
    if (sortCol === col) setSortDir(d => d === 'asc' ? 'desc' : 'asc');
    else { setSortCol(col); setSortDir('asc'); }
  };

  const filtered = useMemo(() => {
    let list = [...data];
    if (searchText.trim()) {
      const term = searchText.trim().toLowerCase();
      list = list.filter(k => k.name.toLowerCase().includes(term));
    }
    if (activeSegments.length > 0) {
      list = list.filter(k => activeSegments.includes(k.segment));
    }
    if (activeFulfillment.length > 0) {
      list = list.filter(k => activeFulfillment.includes(k.fulfillment_status || 'unbekannt'));
    }
    if (standortFilter) {
      list = list.filter(k => String(k.standort_id) === standortFilter);
    }
    list.sort((a, b) => {
      let va, vb;
      switch (sortCol) {
        case 'name': va = a.name.toLowerCase(); vb = b.name.toLowerCase(); break;
        case 'segment': va = a.segment || 'Z'; vb = b.segment || 'Z'; break;
        case 'standort': va = (a.standort_name || '').toLowerCase(); vb = (b.standort_name || '').toLowerCase(); break;
        case 'sop': va = Number(a.sop_anteil || 0); vb = Number(b.sop_anteil || 0); break;
        case 'auftraege': va = Number(a.auftraege_30d || 0); vb = Number(b.auftraege_30d || 0); break;
        case 'db': va = Number(a.db_pct_30d || 0); vb = Number(b.db_pct_30d || 0); break;
        case 'pick': va = Number(a.picking_time_per_paket || 0); vb = Number(b.picking_time_per_paket || 0); break;
        case 'pack': va = Number(a.packing_time_per_paket || 0); vb = Number(b.packing_time_per_paket || 0); break;
        case 'cutoff': va = Number(a.cutoff_stunde || 0); vb = Number(b.cutoff_stunde || 0); break;
        case 'fulfillment': va = a.fulfillment_status || 'unbekannt'; vb = b.fulfillment_status || 'unbekannt'; break;
        default: va = a.name.toLowerCase(); vb = b.name.toLowerCase();
      }
      if (va < vb) return sortDir === 'asc' ? -1 : 1;
      if (va > vb) return sortDir === 'asc' ? 1 : -1;
      return 0;
    });
    return list;
  }, [data, searchText, activeSegments, activeFulfillment, standortFilter, sortCol, sortDir]);

  const ohnePartner = data.filter(k => !k.odoo_partner_id);

  const SortTh = ({ col, children, className = '' }) => (
    <th onClick={() => toggleSort(col)}
      className={`px-3 py-3 text-[10px] font-medium text-[var(--text-secondary)] uppercase tracking-wider cursor-pointer select-none hover:text-content-secondary transition-colors ${className}`}>
      <span className="flex items-center gap-1">
        {children}
        {sortCol === col && <span className="text-forecast">{sortDir === 'asc' ? '↑' : '↓'}</span>}
      </span>
    </th>
  );

  return (
    <div className="p-page">
      {/* Header */}
      <PageHeader
        title={t('customers.title')}
        subtitle={t('customers.customerCount', { n: filtered.length })}
        className="mb-section"
        actions={
          <div className="flex items-center gap-2">
            {ohnePartner.length > 0 && (
              <Button variant="secondary" size="sm" onClick={() => { setBulkModal(true); setBulkResult(null); }}
                className="bg-cyan-600/15 text-cyan-400 hover:bg-cyan-600/25 border-cyan-500/20">
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M4 4v5h5" /><path d="M20 20v-5h-5" /><path d="M4 9a9 9 0 0114.7-4.7L20 6" /><path d="M20 15a9 9 0 01-14.7 4.7L4 18" />
                </svg>
                {t('customers.odooMatch', { n: ohnePartner.length })}
              </Button>
            )}
            <Button variant="secondary" size="sm" onClick={() => setModal('new')}>
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M12 5v14M5 12h14"/></svg>
              {t('customers.newCustomer')}
            </Button>
          </div>
        }
      />

      {/* Filter bar */}
      <div className="flex flex-wrap items-center gap-3 mb-4">
        <div className="relative flex-1 max-w-xs">
          <svg className="absolute left-3 top-1/2 -translate-y-1/2 text-[var(--text-tertiary)]" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="11" cy="11" r="8"/><path d="M21 21l-4.35-4.35"/></svg>
          <input value={searchText} onChange={e => setSearchText(e.target.value)} placeholder={t('customers.searchCustomer')}
            className="w-full pl-9 pr-3 py-2 bg-surface-inset border border-line-hover rounded-lg text-sm text-content placeholder-content-dim focus:outline-none focus:ring-1 focus:ring-primary-500/30" />
        </div>
        <div className="flex items-center gap-1.5">
          {['A', 'B', 'C'].map(seg => (
            <Button key={seg} variant={activeSegments.includes(seg) ? 'primary' : 'ghost'} size="xs" onClick={() => toggleSegment(seg)}
              className={activeSegments.includes(seg) ? (seg === 'A' ? '!bg-primary-500/15 !text-primary-400' : seg === 'B' ? '!bg-purple-500/15 !text-purple-400' : '!bg-amber-500/15 !text-amber-400') : ''}>
              {seg}
            </Button>
          ))}
        </div>
        <div className="w-px h-5 bg-line" />
        <div className="flex items-center gap-1.5">
          {[
            { key: 'aktiv', label: t('customers.fulfillmentAktiv'), active: '!bg-emerald-500/15 !text-emerald-500' },
            { key: 'ruhend', label: t('customers.fulfillmentRuhend'), active: '!bg-amber-500/15 !text-amber-500' },
            { key: 'inaktiv', label: t('customers.fulfillmentInaktiv'), active: '!bg-gray-500/15 !text-[var(--text-tertiary,#6B7280)]' },
          ].map(f => (
            <Button key={f.key} variant={activeFulfillment.includes(f.key) ? 'primary' : 'ghost'} size="xs" onClick={() => toggleFulfillment(f.key)}
              className={activeFulfillment.includes(f.key) ? f.active : ''}>
              {f.label}
            </Button>
          ))}
        </div>
        <div className="w-px h-5 bg-line" />
        <select value={standortFilter} onChange={e => setStandortFilter(e.target.value)}
          className="px-3 py-1.5 border border-[var(--border-card,#E5E7EB)] rounded-lg text-xs bg-[var(--bg-card,#fff)] text-[var(--text-primary)]">
          <option value="">{t('customers.alleStandorte')}</option>
          {standorte.map(s => (
            <option key={s.id} value={s.id}>{s.name}{s.betriebsmodell === 'joint_venture' ? ' (JV)' : ''}</option>
          ))}
        </select>
        <span className="text-xs text-[var(--text-secondary)] font-mono ml-auto">{filtered.length} / {data.length}</span>
      </div>

      <div className="bg-surface-card border border-line rounded-card overflow-hidden shadow-card">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-line text-left">
                <SortTh col="name">{t('customers.customerCol')}</SortTh>
                <SortTh col="segment">{t('customers.segCol')}</SortTh>
                <SortTh col="fulfillment">{t('customers.statusCol')}</SortTh>
                <SortTh col="standort">{t('customers.locationCol')}</SortTh>
                <SortTh col="sop" className="text-right">{t('customers.sopPctCol')}</SortTh>
                <SortTh col="auftraege" className="text-right">{t('customers.orders30dCol')}</SortTh>
                <SortTh col="db" className="text-right">{t('customers.dbPct30dCol')}</SortTh>
                <SortTh col="pick" className="text-right">{t('customers.pickCol')}</SortTh>
                <SortTh col="pack" className="text-right">{t('customers.packCol')}</SortTh>
                <SortTh col="cutoff" className="text-right">{t('customers.cutoffCol')}</SortTh>
                <th className="px-3 py-3 text-[10px] font-medium text-[var(--text-secondary)] uppercase tracking-wider">{t('customers.odooCol')}</th>
                <th className="px-3 py-3 w-16"></th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((k) => {
                const sopPct = Number(k.sop_anteil || 0) * 100;
                const dbPct = Number(k.db_pct_30d || 0);
                const auftr = Number(k.auftraege_30d || 0);
                return (
                  <tr key={k.id} onClick={() => navigate(`/kunden/${k.id}`)}
                    className="border-b border-line hover:bg-surface-hover transition-colors cursor-pointer">
                    <td className="px-3 py-2.5 font-medium text-content">{k.name}</td>
                    <td className="px-3 py-2.5"><Badge variant={k.segment === 'A' ? 'segment-a' : k.segment === 'B' ? 'segment-b' : k.segment === 'C' ? 'segment-c' : 'default'} className="text-[10px] font-bold">{k.segment || '-'}</Badge></td>
                    <td className="px-3 py-2.5"><StatusBadge status={k.fulfillment_status} /></td>
                    <td className="px-3 py-2.5 text-xs text-content-secondary">{k.standort_name || '–'}</td>
                    <td className="px-3 py-2.5 font-mono text-xs text-right">
                      {sopPct > 0 ? <span className="text-content">{sopPct.toFixed(0)}%</span> : <span className="text-[var(--text-tertiary)]">–</span>}
                    </td>
                    <td className="px-3 py-2.5 font-mono text-xs text-right text-content-secondary">{auftr > 0 ? auftr : '–'}</td>
                    <td className="px-3 py-2.5 text-right">
                      {dbPct !== 0 ? <span className="inline-flex items-center gap-1 text-[10px] font-bold text-[var(--text-primary)]"><span className={`w-2 h-2 rounded-full ${dbPct >= 25 ? 'bg-green-500' : dbPct >= 15 ? 'bg-yellow-500' : dbPct >= 10 ? 'bg-orange-500' : 'bg-red-500'}`} />{dbPct}%</span> : <span className="text-[10px] text-[var(--text-tertiary)]">–</span>}
                    </td>
                    <td className="px-3 py-2.5 font-mono text-xs text-right text-content">{Number(k.picking_time_per_paket)}</td>
                    <td className="px-3 py-2.5 font-mono text-xs text-right text-content">{Number(k.packing_time_per_paket)}</td>
                    <td className="px-3 py-2.5 font-mono text-xs text-right text-content-secondary">{Number(k.cutoff_stunde)}:00</td>
                    <td className="px-3 py-2.5">
                      {k.odoo_partner_id ? (
                        <span className="px-1.5 py-0.5 text-[10px] font-mono rounded bg-surface-inset text-[var(--text-primary)]">{k.odoo_partner_id}</span>
                      ) : <span className="text-[10px] text-[var(--text-tertiary)]">–</span>}
                    </td>
                    <td className="px-3 py-2.5" onClick={e => e.stopPropagation()}>
                      <div className="flex gap-1">
                        <button onClick={() => setModal(k)} className="p-1.5 rounded hover:bg-surface-active text-[var(--text-tertiary)] hover:text-content transition-colors"><svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M11 4H4a2 2 0 00-2 2v14a2 2 0 002 2h14a2 2 0 002-2v-7"/><path d="M18.5 2.5a2.121 2.121 0 013 3L12 15l-4 1 1-4 9.5-9.5z"/></svg></button>
                        <button onClick={() => remove(k.id)} className="p-1.5 rounded hover:bg-red-500/20 text-[var(--text-tertiary)] hover:text-red-400 transition-colors"><svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M3 6h18M19 6v14a2 2 0 01-2 2H7a2 2 0 01-2-2V6m3 0V4a2 2 0 012-2h4a2 2 0 012 2v2"/></svg></button>
                      </div>
                    </td>
                  </tr>
                );
              })}
              {filtered.length === 0 && (
                <tr><td colSpan={12} className="px-4 py-8 text-center text-[var(--text-tertiary)]">{data.length === 0 ? t('customers.noCustomers') : t('customers.noResults')}</td></tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Edit/Create Modal */}
      <Modal open={modal !== null} onClose={() => setModal(null)} title={modal && modal !== 'new' ? t('customers.editCustomer') : t('customers.newCustomerTitle')}>
        <KundenForm initial={modal !== 'new' ? modal : null} standorte={standorte} onSave={save} onCancel={() => setModal(null)} t={t} />
      </Modal>

      {/* Bulk-Match Modal */}
      <Modal open={bulkModal} onClose={() => setBulkModal(false)} title={t('customers.bulkMatchTitle')} wide>
        <div className="space-y-4">
          <p className="text-xs text-[var(--text-secondary)]">
            {t('customers.bulkMatchDesc')}
          </p>

          {!bulkResult && (
            <div className="flex items-center gap-3">
              <span className="text-sm text-content">{t('customers.customersWithoutOdoo', { n: ohnePartner.length })}</span>
              <button onClick={runBulkMatch} disabled={bulkLoading}
                className="px-4 py-2 rounded-lg text-sm font-medium bg-cyan-600 hover:bg-cyan-500 text-white transition-colors disabled:opacity-50 flex items-center gap-2">
                {bulkLoading ? (<><span className="animate-spin h-4 w-4 border-2 border-white border-t-transparent rounded-full" />{t('customers.matching')}</>) : t('customers.matchNow')}
              </button>
            </div>
          )}

          {bulkResult && (
            <div className="space-y-3">
              <div className={`rounded-lg px-4 py-3 text-sm font-medium ${
                bulkResult.matched > 0 ? 'bg-emerald-500/10 border border-emerald-500/20 text-emerald-400' : 'bg-surface-card border border-line text-content-secondary'
              }`}>{bulkResult.message}</div>

              {bulkResult.manual && bulkResult.manual.length > 0 && (
                <div className="space-y-2">
                  <h4 className="text-xs font-semibold text-[var(--text-secondary)] uppercase tracking-wider">{t('customers.manualAssign')}</h4>
                  {bulkResult.manual.map(m => (
                    <div key={m.kunde_id} className="bg-surface-card border border-line rounded-card p-3 shadow-card">
                      <div className="flex items-center justify-between mb-2">
                        <span className="text-sm font-medium text-content">{m.kunde_name}</span>
                        {m.status === 'zugewiesen' ? (
                          <span className="text-[10px] text-emerald-400 bg-emerald-500/10 px-2 py-0.5 rounded-full">{t('customers.assigned', { id: m.zugewiesene_id })}</span>
                        ) : m.status === 'kein_treffer' ? (
                          <span className="text-[10px] text-[var(--text-secondary)]">{t('customers.noOdooResult')}</span>
                        ) : null}
                      </div>
                      {m.treffer && m.treffer.length > 0 && m.status !== 'zugewiesen' && (
                        <div className="space-y-1">
                          {m.treffer.map(tr => (
                            <button key={tr.id} onClick={() => assignManual(m.kunde_id, tr.id)}
                              className="w-full text-left px-2.5 py-1.5 rounded text-xs hover:bg-surface-hover text-content-secondary transition-colors flex items-center justify-between">
                              <span>{tr.name}</span>
                              <span className="text-[10px] text-[var(--text-secondary)] font-mono">ID: {tr.id} – {t('customers.assignBtn')}</span>
                            </button>
                          ))}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              )}

              <button onClick={() => { setBulkResult(null); }}
                className="text-xs text-[var(--text-secondary)] hover:text-content-secondary transition-colors">{t('customers.searchAgain')}</button>
            </div>
          )}
        </div>
      </Modal>
    </div>
  );
}
